import React, { useEffect } from 'react';
import DashboardContainer from 'views/Dashboard/DashboardContainer';
import styles from './activityDataEntry.module.scss';
import headerStyles from './adeHeader.module.scss';
import TabsComponent from 'shared/components/TabsComponent';
import { DashboardContainerColorProps } from 'enums/DashboardContainerEnums';
import { useState } from 'react';
import BaseYearChildrenComponent from './BaseYearChildrenComponent';
import ActivityDataEntryService from 'services/ActivityDataEntryService';
import { getYearOptions } from 'shared/utils/currentYearsList';
import LoaderAnimation from 'shared/components/LoaderAnimation';
import { useParentOrganizationContext } from 'context/ParentOrganisationContext';
import { useNavigate, useParams } from 'react-router-dom';
import { AppRoutes } from 'routes/routeConstants/appRoutes';
import { getMonthName } from 'shared/utils/monthName';
import { useUserRole } from 'shared/hooks/useUserRole';
import { isViewer, canDelete } from 'shared/utils/roleUtils';
import Notification from 'shared/components/Notification';
import { NotificationTypes } from 'enums/notificationTypes';
import {
  activeTabQuery,
  addYearButton,
  baseYear,
  defaultYearTab,
  deleteLocationYearTitle,
  isEditViewQuery,
  isFirstFlowQuery,
  isProcessingFacility,
  processingFacility,
  scopeTab,
} from 'constants/constants';
import LocationDetails from './LocationDetails';
import DeleteLocationModal from './DeleteLocationModal';
import useFilters from 'shared/hooks/useFilters';
import IsCompletedModal from './IsCompletedModal';
import { LocationYears } from 'models/ActivityDataLocationYears/locationYears.model';
import { monthList } from 'views/CompanySetup/companySetupConstants';
import OptionsDropdown from 'shared/components/OptionsDropdown';
import { stopBubbling } from 'shared/utils/stopBubbling';
import useRedirect from 'shared/utils/useRedirect';
import { CompanySetUpTabHash } from 'enums/CompanySetupEnums';
import {
  getInitialValues,
  getYearLabel,
  handleSubmit,
  handleTabChange,
  handleYearChange,
  handleProceedClick,
} from './activityDataEntryUtils';
import ActivityDataEntryModals from './ActivityDataEntryModals';
import ActivityDataEntryHeader from './ActivityDataEntryHeaderComponent';
import LocationService from 'services/ActivityDataEntryService/location.service';
import clsx from 'clsx';

export interface AddYearValues {
  fromMonth: string;
  fromYear: string;
  toMonth: string;
  toYear: string;
}

// Define the type for the scope mappings
export interface ScopeMap {
  [fuelName: string]: string | null;
}

export const queryParamsList = [
  activeTabQuery,
  isEditViewQuery,
  isFirstFlowQuery,
  isProcessingFacility,
  scopeTab,
];

const ActivityDataEntry = () => {
  const {
    loading,
    createDesiredScopeData,
    scope1Details,
    scope2Details,
    productionData,
    createScopeForLocation,
    fetchOrgMetaData,
    orgListMeta,
    scopeThreeData,
    updateProductionData,
  } = ActivityDataEntryService();
  const {
    scopeLoading,
    yearLoading,
    deleteLoading,
    fetchLocation,
    location,
    createYearForLocation,
    fetchYearForLocation,
    locationScopeDetails,
    updateLocation,
    deleteLocation,
    deleteYearForLocation,
    deleteLocationloading,
  } = LocationService();
  // Will use this once the flow is ready to get base month
  const { organizationData } = useParentOrganizationContext();
  const { navigateBack, redirectToCompanySetUp } = useRedirect();

  const initialScope = 'scope1';
  const { getParams, setParams } = useFilters();

  const {
    activeTab: initialActiveTab,
    isEditView: isEditViewComponent,
    isFirstFlow,
    scopeTab,
  } = getParams(queryParamsList);

  const [activeScope, setActiveScope] = useState(scopeTab ?? initialScope);
  const [activeYear, setActiveYear] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAlert, setIsAlert] = useState(false);
  const [isEditView, setIsEditView] = useState(false);
  const [isDeleteModal, setIsDeleteModal] = useState(false);
  const [isCompletedModal, setIsCompletedModal] = useState(false);
  // for create product modal
  const [isCreateProductModal, setIsCreateProductModal] = useState(false);
  // for cancel modal
  const [isCancelModal, setIsCancelModal] = useState(false);

  const yearOptions = getYearOptions();
  const userRole = useUserRole();
  const isViewerRole = isViewer(userRole);
  const canDeleteLocation = canDelete(userRole);

  const { id: locationId } = useParams();

  const navigate = useNavigate();

  const { startMonth, endMonth } = getMonthName(
    organizationData?.company?.reportingMonth ?? 1
  );

  useEffect(() => {
    fetchLocation(Number(locationId));
    fetchOrgMetaData();
    setIsEditView(!!isEditViewComponent);
  }, [isEditViewComponent]);

  useEffect(() => {
    createDesiredScopeData(locationScopeDetails);
  }, [locationScopeDetails]);

  useEffect(() => {
    initialActiveTab && setActiveYear(initialActiveTab?.toString());
    if (initialActiveTab !== defaultYearTab) {
      fetchYearForLocation(locationId, initialActiveTab?.toString());
    }
  }, [initialActiveTab]);

  const initialValues: AddYearValues = getInitialValues(
    startMonth,
    baseYear,
    endMonth
  );

  const isCompleted = location?.yearsWithScopeStatus?.some(
    item => item?.status
  );

  const handleDeleteYear = (year?: number) => async () => {
    if (isViewerRole) {
      Notification({
        message: "You don't have admin access to add location and year",
        description: '',
        type: NotificationTypes.WARNING,
      });
      return;
    }
    if (locationId && year) {
      const response = await deleteYearForLocation(locationId, year);
      if (!response) return;
      await fetchLocation(Number(locationId));
      handleTabChangeWrapper(defaultYearTab);
    }
  };

  const handleSubmitWrapper = async (values: AddYearValues) => {
    if (isViewerRole) {
      Notification({
        message: "You don't have admin access to add location and year",
        description: '',
        type: NotificationTypes.WARNING,
      });
      setIsModalOpen(false);
      return;
    }
    await handleSubmit(
      values,
      locationId,
      createYearForLocation,
      setIsModalOpen,
      setActiveYear,
      setActiveScope,
      initialScope,
      fetchLocation,
      setParams,
      isFirstFlow
    );
  };

  const handleTabChangeWrapper = (key: string) => {
    if (isViewerRole && key === addYearButton) {
      Notification({
        message: "You don't have admin access to add location and year",
        description: '',
        type: NotificationTypes.WARNING,
      });
      return;
    }
    handleTabChange(
      key,
      isEditView,
      setIsAlert,
      setIsModalOpen,
      addYearButton,
      defaultYearTab,
      setActiveYear,
      setParams,
      isFirstFlow,
      scopeTab,
      initialScope,
      fetchYearForLocation,
      locationId
    );
  };

  const filteredLocations =
    location?.yearsWithScopeStatus?.filter(item => item?.isActive) ?? [];

  const yearLabel = (year: LocationYears) =>
    getYearLabel(
      year,
      startMonth,
      endMonth,
      filteredLocations,
      isEditViewComponent,
      handleDeleteYear,
      deleteLoading,
      deleteLocationYearTitle,
      () => stopBubbling,
      styles,
      OptionsDropdown,
      isViewerRole
    );

  const yearsTabs = filteredLocations?.map(year => {
    return {
      key: year.year?.toString(),
      label: yearLabel(year),
      children: (
        <BaseYearChildrenComponent
          activeScope={activeScope}
          setActiveScope={setActiveScope}
          setIsEditView={setIsEditView}
          isEditView={isEditView}
          scope1Details={scope1Details}
          scope2Details={scope2Details}
          productionData={productionData}
          activeYear={activeYear}
          locationScopeDetails={locationScopeDetails}
          fetchYearForLocation={fetchYearForLocation}
          createDesiredScopeData={createDesiredScopeData}
          createScopeForLocation={createScopeForLocation}
          updateProductionData={updateProductionData}
          scopeLoading={scopeLoading}
          setIsAlert={setIsAlert}
          isEditViewComponent={isEditViewComponent}
          scopeThreeData={scopeThreeData}
          location={location}
        />
      ),
    };
  });

  const handleYearChangeWrapper = (
    value: string,
    setFieldValue: (
      field: string,
      value: string,
      shouldValidate?: boolean | undefined
    ) => void
  ) => {
    handleYearChange(value, setFieldValue, startMonth, monthList);
  };

  const handleProceedClickWrapper = () => {
    handleProceedClick(
      isEditView,
      setIsCancelModal,
      isCompleted,
      setIsCompletedModal,
      location,
      processingFacility,
      setIsCreateProductModal,
      redirectToCompanySetUp,
      CompanySetUpTabHash
    );
  };

  const handleCancelClick = () => {
    if (isEditView) setIsCancelModal(true);
    else navigate(AppRoutes.COMPANY_SETUP);
  };

  const handleBack = () => navigateBack();

  const handleCancelModalClose = () => setIsCancelModal(false);

  const hanldeProductRedirection = () => navigate(AppRoutes.PRODUCT);

  const handleDelete = () => {
    if (!canDeleteLocation) {
      Notification({
        message: "You don't have admin access to add location and year",
        description: '',
        type: NotificationTypes.WARNING,
      });
      return;
    }
    setIsDeleteModal(true);
  };

  return (
    <DashboardContainer bgColor={DashboardContainerColorProps.GREY}>
      {loading ? (
        <div className={styles.loadingContainer}>
          <LoaderAnimation />
        </div>
      ) : (
        <div className={clsx(styles.container, headerStyles.container)}>
          <ActivityDataEntryHeader
            locationName={location?.locationName}
            locationCategory={location?.locationCategory}
            isFirstFlow={isFirstFlow}
            isEditView={isEditView}
            handleBack={handleBack}
            handleCancelClick={handleCancelClick}
            handleProceedClickWrapper={handleProceedClickWrapper}
            handleDelete={handleDelete}
            canDeleteLocation={canDeleteLocation}
          />
          <div className={styles.yearTabContainer}>
            <TabsComponent
              haveAddButton
              onTabChange={key => handleTabChangeWrapper(key)}
              isMainTab
              activeTab={activeYear}
              haveLocationTab
              locationChildren={
                <LocationDetails
                  scopeLoading={scopeLoading}
                  locationDetails={location}
                  orgListMeta={orgListMeta}
                  updateLocation={updateLocation}
                  fetchLocation={fetchLocation}
                />
              }
              tabs={yearsTabs ? yearsTabs : []}
            />
          </div>
        </div>
      )}
      <ActivityDataEntryModals
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        initialValues={initialValues}
        yearOptions={yearOptions}
        handleYearChangeWrapper={handleYearChangeWrapper}
        handleSubmitWrapper={handleSubmitWrapper}
        yearLoading={yearLoading}
        isAlert={isAlert}
        setIsAlert={setIsAlert}
        isCreateProductModal={isCreateProductModal}
        setIsCreateProductModal={setIsCreateProductModal}
        hanldeProductRedirection={hanldeProductRedirection}
        handleBack={handleBack}
        isCancelModal={isCancelModal}
        handleCancelModalClose={handleCancelModalClose}
      />
      <DeleteLocationModal
        setIsModalOpen={setIsDeleteModal}
        isModalOpen={isDeleteModal}
        deleteLocation={deleteLocation}
        deleteLoading={deleteLocationloading}
      />
      <IsCompletedModal
        setIsModalOpen={setIsCompletedModal}
        isModalOpen={isCompletedModal}
      />
    </DashboardContainer>
  );
};

export default ActivityDataEntry;
